import React from 'react'
import {
  CustomColumnHeaderRendererProps,
  doFiltersHaveOptions,
  doFiltersHaveSelectionsOnGroups
} from '../../legacy-design-system/grid'
import { RouteContext } from '../route-provider'
import { Filters, FilterConfig } from '../../legacy-design-system/filter'
import {
  FilterConfigs,
  FilterSelections
} from '../../legacy-design-system/filter/filters'
import { CustomApplicationAggregation, MetadataFilter } from './index'

const appMetdataToken = 'application-metadata-'
export const getAppMetadataGroupID = (configs, groupID) =>
  `${appMetdataToken}${configs[groupID]?.groupName?.id}`

interface CustomSelectionType {
  field?: string
  fieldID?: string
  groupID?: string
  optionIDs?: string[]
}
export const iterateSelections = (
  queryParameters, // Map of url key:values
  customAggregations, // config for metadata fields
  customAggregationSelections // current metadata selections
): CustomSelectionType[] => {
  const res: CustomSelectionType[] = []

  queryParameters.forEach((optionIDs, fieldID) => {
    const { id: groupID } =
      customAggregations.find(
        ({ label }) => fieldID.replace(appMetdataToken, '') === label
      ) || {}
    const field = customAggregationSelections.find(
      (it) => it.fieldID === groupID
    )

    res.push({ field, fieldID, groupID, optionIDs })
  })

  return res
}

interface MetadataHeaderRendererProps<TAggregationCategory extends string>
  extends CustomColumnHeaderRendererProps<TAggregationCategory> {
  readonly customAggregations: CustomApplicationAggregation[]
  readonly customAggregationSelections: MetadataFilter[]
  readonly onMetadataOptionsToggle: (groupID: string, values: string[]) => void
  readonly onClearMetadataSelections: () => void
}
export function metadataHeaderRenderer<TAggregationCategory extends string>({
  renderContent,
  filters,
  clearAll,
  customAggregations,
  customAggregationSelections,
  onMetadataOptionsToggle,
  onClearMetadataSelections
}: MetadataHeaderRendererProps<TAggregationCategory>) {
  //pick up query parameters for this filter on initial load
  // eslint disables here are in lieu of renaming this component
  // and keeping the effect running only once
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const routeCtx = React.useContext(RouteContext)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    // wait for it to be populated
    if (!customAggregations?.length) {
      return
    }
    // read url for application-metatadata params and sync with current metadata selections
    iterateSelections(
      routeCtx.queryParameters,
      customAggregations,
      customAggregationSelections
    ).forEach(
      ({ field, groupID, optionIDs }) =>
        !field &&
        groupID &&
        optionIDs &&
        onMetadataOptionsToggle(groupID, optionIDs)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customAggregations])

  const isFilterable =
    !!filters &&
    filters.selections &&
    (doFiltersHaveOptions(filters.groupConfigs, filters.groupIDs) ||
      doFiltersHaveSelectionsOnGroups(filters.selections, filters.groupIDs))
  const isFiltered =
    !!filters &&
    !!filters.selections &&
    filters.groupIDs &&
    filters.groupIDs.some((groupID) =>
      filters &&
      filters.selections &&
      filters.selections[groupID] &&
      filters.selections[groupID].length > 0
        ? true
        : false
    )

  function HeaderContent({
    isOpen,
    isClickable
  }: {
    readonly isOpen: boolean
    readonly isClickable: boolean
  }) {
    return renderContent({
      isClickable,
      isOpen,
      isFilterable: isFilterable || customAggregations.length > 0,
      isFiltered: isFiltered || customAggregationSelections.length > 0,
      onClear: () => {
        if (clearAll) {
          clearAll()
        }

        // write clear with url
        iterateSelections(
          routeCtx.queryParameters,
          customAggregations,
          customAggregationSelections
        ).forEach(
          ({ field, fieldID, optionIDs }) =>
            field &&
            fieldID &&
            optionIDs &&
            optionIDs.forEach(
              (optionID) =>
                filters?.onOptionToggle &&
                filters.onOptionToggle({ groupID: fieldID, optionID })
            )
        )

        onClearMetadataSelections()
      }
    })
  }

  if (filters && (isFilterable || customAggregations)) {
    const customAggregationConfigs: FilterConfigs = customAggregations
      .map(({ id, label, buckets }) => {
        const hasEmail =
          buckets.length > 0 &&
          !!buckets[0].key.subfieldValues &&
          buckets[0].key.subfieldValues.length > 0 &&
          buckets[0].key.subfieldValues.some(
            ({ fieldType }) => fieldType === 'EMAIL'
          )

        const config: FilterConfig = {
          id,
          groupName: { id: label, defaultMessage: label },
          optionToString: ({ key }) => {
            if (key.subfieldValues) {
              const name = key.subfieldValues.find(
                ({ fieldType }) => fieldType === 'CONTACT_NAME'
              )

              return !name ? key.value : name.value
            }
            return key.value
          },
          optionToID: ({ key }) => key.id,
          optionToTooltipContent: hasEmail
            ? ({ key }) => {
                if (!key.subfieldValues || key.subfieldValues.length === 0) {
                  return key.value
                }

                const email = key.subfieldValues.find(
                  ({ fieldType }) => fieldType === 'EMAIL'
                )

                return !email ? key.value : email.value
              }
            : undefined,
          options: buckets
        }
        return {
          [id]: config
        }
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {})

    const customAggregationFilterSelections: FilterSelections =
      customAggregations
        .map(({ id }) => {
          const selectionGroup = customAggregationSelections.find(
            ({ fieldID }) => fieldID === id
          )
          return { [id]: (selectionGroup && selectionGroup.values) || [] }
        })
        .reduce((acc, curr) => ({ ...acc, ...curr }), {})

    const customGroupIDs = customAggregations.map(({ id }) => id)

    return (
      <Filters
        groupConfigs={{ ...filters.groupConfigs, ...customAggregationConfigs }}
        selections={{
          ...filters.selections,
          ...customAggregationFilterSelections
        }}
        groupIDs={[...filters.groupIDs, ...customGroupIDs]}
        onGroupToggle={({ optionsAvailableForSelection, ...props }) => {
          const groupID = props.groupID as TAggregationCategory
          if (customGroupIDs.indexOf(groupID) > -1) {
            const currentSelections = customAggregationFilterSelections[groupID]
            const options =
              currentSelections.length === 0 ||
              currentSelections.length === optionsAvailableForSelection.length
                ? optionsAvailableForSelection
                : optionsAvailableForSelection.filter(
                    (option) => currentSelections.indexOf(option) === -1
                  )

            onMetadataOptionsToggle(groupID, options)
            // write metadata filter toggle to URL
            if (filters.onGroupToggle) {
              filters.onGroupToggle({
                groupID: getAppMetadataGroupID(
                  customAggregationConfigs,
                  groupID
                ),
                optionsAvailableForSelection: options
              })
            }
          } else {
            if (!!filters.onGroupToggle) {
              filters.onGroupToggle({ groupID, optionsAvailableForSelection })
            }
          }
        }}
        onOptionToggle={({ optionID, ...props }) => {
          const groupID = props.groupID as TAggregationCategory
          if (customGroupIDs.indexOf(groupID) > -1) {
            onMetadataOptionsToggle(groupID, [optionID])
            // write metadata filter toggle to URL
            if (filters.onOptionToggle) {
              filters.onOptionToggle({
                groupID: getAppMetadataGroupID(
                  customAggregationConfigs,
                  groupID
                ),
                optionID
              })
            }
          } else {
            if (!!filters.onOptionToggle) {
              filters.onOptionToggle({ groupID, optionID })
            }
          }
        }}
      >
        {({ isOpen, isTransitioning }) => {
          return (
            <HeaderContent isOpen={isOpen} isClickable={!isTransitioning} />
          )
        }}
      </Filters>
    )
  }

  return <HeaderContent isOpen={false} isClickable={false} />
}
